"use client";

import BookmarkSquareIcon from "@heroicons/react/24/solid/BookmarkSquareIcon";
import CalendarDaysIcon from "@heroicons/react/24/solid/CalendarDaysIcon";
import MapPinIcon from "@heroicons/react/24/solid/MapPinIcon";
import ArrowRightIcon from "@heroicons/react/24/solid/ArrowRightIcon";
import HeartIcon from "@heroicons/react/24/solid/HeartIcon";
import OutlineBookmarkSquareIcon from "@heroicons/react/24/outline/BookmarkSquareIcon";
import OutlineCalendarDaysIcon from "@heroicons/react/24/outline/CalendarDaysIcon";
import OutlineMapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import OutlinePowerIcon from "@heroicons/react/24/outline/PowerIcon";
import OutlineTicketIcon from "@heroicons/react/24/outline/TicketIcon";
import OutlineWrenchScrewdriverIcon from "@heroicons/react/24/outline/WrenchScrewdriverIcon";
import TicketIcon from "@heroicons/react/24/solid/TicketIcon";
import HomeIcon from "@heroicons/react/24/solid/HomeIcon";
import OutlineHomeIcon from "@heroicons/react/24/outline/HomeIcon";
import OutlineUserGroupIcon from "@heroicons/react/24/outline/UserGroupIcon";
import UserGroupIcon from "@heroicons/react/24/solid/UserGroupIcon";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/CustomLink";
import { SidebarButtons } from "@/components/views/modals/sidebar/SidebarButtons";
import { ROUTES } from "@/@core/constants/routes";
import ListSocialPlatforms from "@/components/views/modals/sidebar/content/social-platforms";
import Header from "@/components/views/modals/sidebar/content/header";
import WelcomeCard from "@/components/views/modals/sidebar/content/welcome-card";
import SidebarAddVenueButton from "@/components/views/modals/sidebar/SidebarAddVenue";
import { signOut, useSession } from "next-auth/react";
import { useEffect } from "react";
import { useActionState } from "react";
import { useSite } from "@/lib/hooks/site";
import { useTranslation } from "@/hooks/translationContext";
import { usePlausible } from "next-plausible";
import { CLICK_QUOTE_EVENT_SECTIONS, PLAUSIBLE_GOAL_KEYS } from "@/@core/constants/plausible";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";
import { signOutAction } from "@/actions/signout";
import MapIcon from "@heroicons/react/24/solid/MapIcon";
import OutlineMapIcon from "@heroicons/react/24/outline/MapIcon";
import { useViewMode } from "@/context/ViewModeContext";
import { useRouter } from "next/navigation";
import { useSiteCompanyGlobalDiscount } from "@/hooks/useSiteCompanyGlobalDiscount";

interface Props {
  onClose: () => void;
}

export function SidebarContent({ onClose }: Props) {
  const { data, update, status } = useSession();

  const user = data?.user;

  // Fills the viewport: header and footer keep their size, the link list takes
  // the remaining space so the whole menu fits on screen without scrolling.
  return (
    <div className="flex flex-col gap-2 h-full min-h-0">
      <Header onClose={onClose} />

      <main className="flex flex-col gap-3 flex-1 min-h-0">
        <WelcomeCard user={user} />

        {/*<SidebarAddVenueButton onClose={onClose}/>*/}

        {/*<GetOfferButton onClose={onClose}/>*/}

        <div className="w-full border-b border-slate-300 flex-col justify-start items-start gap-2 inline-flex">
          {status === "authenticated" ? (
            <UserLinks onClose={onClose} />
          ) : (
            <SidebarButtons onClose={onClose} update={update} />
          )}
        </div>

        <Links onClose={onClose} />
      </main>

      <footer className="shrink-0 pb-2">
        <div className="flex gap-2 items-center w-full">
          <SidebarAddVenueButton onClose={onClose} />

          <GetOfferButton onClose={onClose} />
        </div>

        <ListSocialPlatforms />
      </footer>
    </div>
  );
}

function UserLinks({ onClose }: { onClose?: () => void }) {
  const [state, dispatch] = useActionState(signOutAction, undefined);

  useEffect(() => {
    if (state === "true") {
      signOut({ redirect: false });
    }
  }, [state]);

  const LINKS = [
    {
      name: "Ayarlar",
      path: ROUTES.SETTINGS,
      icon: OutlineWrenchScrewdriverIcon,
    },
    {
      name: "Favori Mekanlar",
      path: ROUTES.FAVORITE_VENUES,
      icon: HeartIcon,
    },
  ];

  return (
    <div className="flex flex-col gap-2 w-full">
      {LINKS.map((item, index) => (
        <Link href={item.path} key={index} onClick={onClose}>
          <Button
            key={index}
            className="gap-2 bg-transparent hover:bg-transparent w-full border-b border-stone-50 text-primary flex justify-start px-2 py-2 text-sm font-bold"
          >
            <item.icon className="size-5" />
            {item.name}
          </Button>
        </Link>
      ))}

      <form action={dispatch}>
        <Button className="gap-2 bg-transparent hover:bg-transparent border-0 text-secondary flex justify-start px-2 py-2 text-sm font-bold">
          <OutlinePowerIcon className="size-5" />
          Oturumu Kapat
        </Button>
      </form>
    </div>
  );
}

function GetOfferButton({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation()
  const plausible = usePlausible()
  const isAskQuestionEnabled = useIsAskQuestionEnabled()

  const sendPlausibleEvent = () => {
    plausible(PLAUSIBLE_GOAL_KEYS.CLICK_QUOTE_EVENT, {
      props: {
        section: CLICK_QUOTE_EVENT_SECTIONS.SIDEBAR,
      },
    });
  };

  if (!isAskQuestionEnabled) return null;

  return (
    <Link
      className="w-full"
      onClick={() => {
        sendPlausibleEvent();

        onClose();
      }}
      aria-label={`${t("quote-button-title")}`}
      href={ROUTES.CONTACT_WITH_VENUES}
    >
      <Button className="w-full" variant="secondary">
        {t("quote-button-title")}
      </Button>
    </Link>
  );
}

function Links({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation()
  const site = useSite();
  const {setViewMode} = useViewMode();
  const router = useRouter();

  // "%30 Şirket/Üniversite/Lise İndirimleri": the percent is the site's company
  // discount rate from the panel; left out when the site has none configured.
  const isCompanyPageDisabled = site?.setting?.disabledPages?.company === 1;
  const companyGlobalDiscount = useSiteCompanyGlobalDiscount(!isCompanyPageDisabled);
  const companyDiscountsLabel = t("sidebar-company-discounts-title", {
    defaultValue: "Şirket/Üniversite/Lise İndirimleri",
  });
  const companyDiscountsTitle = companyGlobalDiscount
    ? `%${companyGlobalDiscount} ${companyDiscountsLabel}`
    : companyDiscountsLabel;

  const routers = [
    {
      name: "Anasayfa",
      icon: OutlineHomeIcon,
      hoverIcon: HomeIcon,
      path: "/",
    },
    {
      name: t("text-menu-venue-filter"),
      icon: OutlineMapPinIcon,
      hoverIcon: MapPinIcon,
      path: `/${site?.setting?.routes?.generalList}`,
    },
    {
      name: companyDiscountsTitle,
      icon: OutlineTicketIcon,
      hoverIcon: TicketIcon,
      path: ROUTES.ALL_COMPANIES,
      isHidden: isCompanyPageDisabled,
    },
    {
      name: t("listpage-map-view-button"),
      icon: OutlineMapIcon,
      hoverIcon: MapIcon,
      onClick: () => {
        router.push(`/${site?.setting?.routes?.generalList}`);
        setViewMode("map");
        onClose();
      },
      disabled: true,
    },
    {
      name: t("text-menu-content-campaign"),
      icon: OutlineTicketIcon,
      hoverIcon: TicketIcon,
      path: ROUTES.CAMPAIGNS,
      isHidden: site?.setting?.disabledPages?.campaign === 1,
    },
    {
      name: t("text-menu-content-event"),
      icon: OutlineCalendarDaysIcon,
      hoverIcon: CalendarDaysIcon,
      path: ROUTES.EVENTS,
      isHidden: site?.setting?.disabledPages?.event === 1,
    },
    {
      name: t("text-menu-user-filter"),
      icon: OutlineUserGroupIcon,
      hoverIcon: UserGroupIcon,
      path: `/${site.setting?.routes?.trainer}`,
      isHidden: site?.setting?.disabledPages?.trainer === 1,
    },
    {
      name: t("text-menu-content-blog"),
      icon: OutlineBookmarkSquareIcon,
      hoverIcon: BookmarkSquareIcon,
      path: ROUTES.BLOG,
    },
  ];

  const filteredRoutes = routers.filter((r) => !r.isHidden);

  function ItemElement({ item }) {
    return (
      <>
        <div className="py-2 flex gap-2 items-center">
          <item.icon className="size-6 group-hover:hidden block" />
          <item.hoverIcon className="size-6 group-hover:block hidden" />
          <span className="text-[14px] font-semibold">{item.name}</span>
        </div>

        <div className="group-hover:block hidden">
          <ArrowRightIcon className="size-5 text-primary" />
        </div>
      </>
    )
  };

  return (
    <div className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto">
      {filteredRoutes.map((item, index) => (
        item.path ? (
          <Link
            href={item.path}
            key={index}
            onClick={onClose}
            className="group hover:flex px-4 hover:justify-between hover:items-center hover:rounded-2xl hover:bg-stone-50"
          >
            <ItemElement item={item} />
          </Link>
        ) : (
          <button
            role="button"
            key={index}
            onClick={item.onClick}
            className="group hover:flex px-4 hover:justify-between hover:items-center hover:rounded-2xl hover:bg-stone-50"
          >
            <ItemElement item={item} />
          </button>
        )
      ))}
    </div>
  );
}
